import * as Sentry from "@sentry/react";
import axios from "axios";
import { initializeApp } from "firebase/app";
import {
	EmailAuthProvider,
	FacebookAuthProvider,
	GoogleAuthProvider,
	OAuthProvider,
	checkActionCode,
	confirmPasswordReset as confirmPasswordResetFirebase,
	createUserWithEmailAndPassword,
	getAuth,
	linkWithCredential,
	linkWithRedirect,
	reauthenticateWithCredential,
	sendEmailVerification,
	sendPasswordResetEmail,
	signInAnonymously,
	// signInWithPopup,
	signInWithEmailAndPassword,
	signInWithPopup,
	signOut,
	updateCurrentUser,
	updatePassword,
	updateProfile,
	verifyPasswordResetCode,
} from "firebase/auth";
import { trackPinterest } from "utils/pinterestTag";
import { EMPTY } from "rxjs";
import setAuthToken from "utils/setAuthToken";
import { isBusinessMode } from "../utils/businessContext";
import trackEvent, { type TrackLocation } from "components/tracking/trackEvent";

const config = {
	apiKey: "AIzaSyDrlQBFWVD8rq17GTpV1utDMUV4--DScvg",
	authDomain: "stippl.firebaseapp.com",
	databaseURL: "https://stippl.firebaseio.com",
	projectId: "stippl",
	storageBucket: "stippl.appspot.com",
	messagingSenderId: "323972166388",
	appId: "1:323972166388:web:09658f92ce083e8b",
	measurementId: "G-GCK8X3C1JC",
};

export const app = initializeApp(config);
export let idToken = "";
export let auth = getAuth(app);
export let user: any = {};

const googleProvider = new GoogleAuthProvider();
const facebookProvider = new FacebookAuthProvider();
const appleProvider = new OAuthProvider("apple.com");

export const getToken = () => user?.getIdToken?.() || EMPTY;

export const updateToken = async (user) => {
	if (user) {
		idToken = await user.getIdToken();
		window.sessionStorage.setItem("StipplToken", idToken);
		setAuthToken(idToken);
	}
};

/**
 * Resolves true when the sign-in redeemed pending trip invites (the user was added to a trip), so the
 * caller can refetch the trips. It used to reload the page, which dropped whatever the user was doing:
 * a follow, like or copy waiting on the share page, a join waiting in JoinTrip.
 */
export const updateAuth = async (newUser): Promise<boolean> => {
	if (newUser) {
		// Sentry gets the uid only: no email or display name leaves the browser for error reporting.
		Sentry.setUser({ id: newUser.uid });
		user = newUser;
		idToken = await newUser.getIdToken();
		const { displayName, email } = newUser;
		if (displayName && email) {
			// Listmonk "register" enrolment now happens backend-side in the GraphQL getUser
			// create-branch (graphql/datasources/userProfile-api.ts), keyed off the canonical
			// Firebase user record instead of this client-supplied displayName/email.
			// The server derives the email from the token now (the body is ignored). Pass the token
			// explicitly: this runs before setAuthToken has necessarily applied the axios default.
			try {
				const res = await axios.post("/api/trips/invite", {}, { headers: { Authorization: idToken } });
				return !!res.data;
			} catch (e: any) {
				console.warn("[Auth] Checking trip invites failed:", { uid: newUser.uid, error: e?.message || e });
			}
		}
	}
	return false;
};

export const loginWithFacebook = async () => {
	const auth = getAuth();
	const user = await signInWithPopup(auth, facebookProvider);
	if (user) {
		trackPinterest("signup", { lead_type: "FB signup" });
	}
};

export const login = async (email, password) => {
	const auth = getAuth();
	const userCredential = await signInWithEmailAndPassword(auth, email, password);
	const token = userCredential.user.getIdToken();
	return { user: userCredential, token };
};

export const loginWithGoogle = async () => {
	const auth = getAuth();
	const user = await signInWithPopup(auth, googleProvider);
	if (user) {
		trackPinterest("signup", { lead_type: "google signup" });
	}
};

export const loginWithApple = async () => {
	const auth = getAuth();
	const user = await signInWithPopup(auth, appleProvider);
	if (user) {
		trackPinterest("signup", { lead_type: "apple signup" });
	}
};

// `location` distinguishes which of the two delete-account surfaces the call came from
// (DeleteUserAccount.tsx's modal vs SettingsPage.tsx's own inline flow) — funnel 169's terminal
// step (ClickUp 123yb692gth) needs to tell them apart, since both call this same util.
export const deleteFirebaseUser = async (location: TrackLocation = "settings") => {
	// No client-side unlinking of providers first: on a PR preview this SDK talks to the production
	// Firebase project, where it stripped the real login's providers (123yb697kxq). The backend's
	// admin SDK deletes the whole user, providers included.
	// delete user in BE to bypass security mechanism "CREDENTIAL_TOO_OLD_LOGIN_AGAIN" When user delete account in FE
	await axios.delete("/api/user");
	// This has to fire here, not in the caller. logout() below ends in window.location.reload(),
	// and the OpenPanel web SDK POSTs with no sendBeacon/keepalive — anything fired after that
	// reload is requested does not survive the navigation. The axios call above is what actually
	// deletes the account, so this is the last point the page is guaranteed alive AND the point
	// the deletion has genuinely succeeded.
	try {
		trackEvent("deletion_confirmed", {}, location);
	} catch {
		// tracking must never affect rendering
	}
	await logout();
};

export const loginAnonymously = async () => {
	const auth = getAuth();
	await signInAnonymously(auth);
};

export const linkToFacebook = async () => {
	const auth = getAuth();
	await linkWithRedirect(auth.currentUser, facebookProvider);
};

export const linkToGoogle = async () => {
	const auth = getAuth();
	await linkWithRedirect(auth.currentUser, googleProvider);
};

export const logout = async () => {
	Sentry.setUser(null);
	const auth = getAuth();
	const { resetRevenueCat } = await import("lib/revenuecat");
	resetRevenueCat();
	await signOut(auth);
	return window.location.reload();
};

export const register = async (name, email, password, link = false) => {
	const auth = getAuth();
	if (link) {
		const credential = EmailAuthProvider.credential(email, password);
		await linkWithCredential(auth.currentUser, credential)
			.then(async (usercred) => {
				const user = usercred.user;
				await updateCurrentUser(auth, user);
				console.log("Anonymous account successfully upgraded", user);
			})
			.catch((error) => {
				console.log("Error upgrading anonymous account", error);
			});
	} else {
		await createUserWithEmailAndPassword(auth, email, password);
	}

	// B2B: Use backend for Listmonk email, B2C: Use Firebase directly
	if (isBusinessMode()) {
		try {
			await axios.post("/api/utils/send_verification", { name });
		} catch (err) {
			console.error("Error sending B2B verification email:", err);
		}
	} else {
		await sendEmailVerification(auth.currentUser);
	}

	await updateProfile(auth.currentUser, {
		displayName: name,
	});
};

export const passwordReset = async (email) => {
	const auth = getAuth();
	await sendPasswordResetEmail(auth, email);
};

export const verifyPasswordReset = async (code) => {
	const auth = getAuth();
	return await verifyPasswordResetCode(auth, code);
};

export const confirmPasswordReset = async (code, newPassword) => {
	const auth = getAuth();
	await confirmPasswordResetFirebase(auth, code, newPassword);
};

export const changePassword = async (password, newPassword) => {
	const auth = getAuth();
	await reauthenticateWithCredential(auth.currentUser, EmailAuthProvider.credential(auth.currentUser.email, password));
	return await updatePassword(auth.currentUser, newPassword);
};

export const verifyEmail = async (code) => {
	const auth = getAuth();
	return await checkActionCode(auth, code);
};

export const getCurrentUsername = () => {
	const auth = getAuth();
	return auth.currentUser?.displayName;
};

const firebase = {
	app,
	auth,
	token: idToken,
	confirmPasswordReset,
	login,
	loginAnonymously,
	loginWithFacebook,
	loginWithGoogle,
	loginWithApple,
	logout,
	register,
	verifyEmail,
	verifyPasswordReset,
	verifyPasswordResetCode,
};

export default firebase;
